import { useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import EmptyState from "../components/EmptyState";
import PageBackBar from "../components/PageBackBar";
import { env } from "../lib/env";
import { useAuth } from "../features/auth/useAuth";
import {
  DEFAULT_PAGE_SIZE,
  DirectoryPagination,
  PageSizeSelect
} from "../features/directory/DirectoryPagination";
import { activationPercent, isActivationChecked } from "../features/directory/activation";
import { extractAxiosDetail, formatAbsolute, formatRelative, fullName } from "../features/directory/format";
import ConfirmDialog from "../features/directory/ConfirmDialog";
import {
  useDebouncedSearch,
  useLocationAdmins,
  useResendWelcomeEmail,
  useWorkvivoDirectoryRefresh
} from "../features/directory/hooks";
import type { DirectorySync, LocationAdmin, LocationAdminStore } from "../features/directory/types";
import UnassignedStoresPanel from "../features/directory/UnassignedStoresPanel";
import WorkvivoSyncBar from "../features/directory/WorkvivoSyncBar";
import "../App.css";

const CHIP_PREVIEW_LIMIT = 6;

function StoreChip({ store }: { store: LocationAdminStore }) {
  return (
    <li
      className={`directory-chip${store.is_active ? "" : " directory-chip--inactive"}`}
      title={store.is_active ? store.business_name : "Inactive location"}
    >
      <span className="directory-chip-name">{store.business_name}</span>
      <span className="directory-chip-meta">{store.store_id}</span>
      {store.is_active ? null : <span className="visually-hidden"> (inactive location)</span>}
    </li>
  );
}

function AdminWorkvivoCell({ admin, syncStatus }: { admin: LocationAdmin; syncStatus: DirectorySync["status"] | undefined }) {
  if (syncStatus === "never") {
    return <span className="directory-muted">Not loaded yet</span>;
  }
  const wv = admin.workvivo;
  const badgeClass =
    wv.active === true
      ? "directory-badge directory-badge--ok"
      : wv.active === false
        ? "directory-badge directory-badge--inactive"
        : "directory-badge directory-badge--muted";
  const label = wv.active === true ? "Active" : wv.active === false ? "Deactivated" : "Not in Workvivo";
  return (
    <div className="directory-stack">
      <span className={badgeClass}>{label}</span>
      {wv.fetched_at ? <span className="directory-muted-small">checked {formatRelative(wv.fetched_at)}</span> : null}
    </div>
  );
}

/** "70% logged in": logged in / active (same as the donuts); hidden until someone is checked. */
function adminActivationLabel(admin: LocationAdmin): string | null {
  const activated = admin.activated_users_managed ?? 0;
  if (activated + (admin.not_activated_users_managed ?? 0) === 0) return null;
  const pct = activationPercent(activated, admin.active_users_managed);
  return pct === null ? null : `${pct}% logged in`;
}

function AdminUsersManagedCell({
  admin,
  syncStatus,
  activationChecked
}: {
  admin: LocationAdmin;
  syncStatus: DirectorySync["status"] | undefined;
  activationChecked: boolean;
}) {
  if (syncStatus === "never") {
    return (
      <div className="directory-stack">
        <span className="directory-muted">—</span>
        <span className="directory-muted-small">load Workvivo data</span>
      </div>
    );
  }
  const activationLabel = activationChecked ? adminActivationLabel(admin) : null;
  return (
    <div className="directory-stack">
      <span className="directory-count">{admin.active_users_managed}</span>
      <span className="directory-muted-small">of {admin.users_managed} in Workvivo</span>
      {activationLabel ? (
        <span
          className="directory-activation-rate"
          title={`${admin.activated_users_managed} logged in, ${admin.not_activated_users_managed} not logged in yet in Workvivo`}
        >
          {activationLabel}
        </span>
      ) : null}
    </div>
  );
}

function StoreChips({ admin }: { admin: LocationAdmin }) {
  const [expanded, setExpanded] = useState(false);
  if (admin.stores.length === 0) {
    return <span className="directory-muted-italic">No locations assigned</span>;
  }
  const visible = expanded ? admin.stores : admin.stores.slice(0, CHIP_PREVIEW_LIMIT);
  const hidden = admin.stores.length - visible.length;
  return (
    <div className="directory-chip-block">
      <ul
        className="directory-chip-list"
        aria-label={`Locations managed by ${fullName(admin.first_name, admin.last_name, admin.email)}`}
      >
        {visible.map((s) => (
          <StoreChip key={s.store_id} store={s} />
        ))}
      </ul>
      {admin.stores.length > CHIP_PREVIEW_LIMIT ? (
        <button
          type="button"
          className="directory-link-btn"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Show fewer" : `+${hidden} more`}
        </button>
      ) : null}
    </div>
  );
}

export default function LocationAdmins() {
  const { user } = useAuth();
  const isSuperuser = user?.role === "superuser";
  const searchId = useId();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [includeInactive, setIncludeInactive] = useState(true);
  const search = useDebouncedSearch(300, () => setPage(0));
  const workvivoRefresh = useWorkvivoDirectoryRefresh();
  const unassignedPanelId = useId();
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [resendFor, setResendFor] = useState<LocationAdmin | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const resend = useResendWelcomeEmail();

  const query = useLocationAdmins(
    { q: search.query || undefined, includeInactive, limit: pageSize, offset: page * pageSize },
    { enabled: isSuperuser }
  );

  // Same guard as Approvals: if the current page fell off the end (data shrank), go back to page 0.
  useEffect(() => {
    const d = query.data;
    if (query.isFetching || !d) return;
    if (d.total > 0 && d.items.length === 0 && d.offset >= d.total) {
      setPage(0);
    }
  }, [query.data, query.isFetching]);

  if (!user || !isSuperuser) {
    return <div style={{ color: "#b91c1c" }}>You don’t have access to view location admins.</div>;
  }

  const changePageSize = (n: number) => {
    setPageSize(n);
    setPage(0);
  };

  const data = query.data;
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const offset = data?.offset ?? 0;
  const hasFilter = search.query !== "" || !includeInactive;
  const rangeText = total === 0 ? "0" : `${offset + 1}–${offset + items.length}`;
  const activationChecked = isActivationChecked(data?.activation_sync);

  return (
    <div className="directory-page">
      <PageBackBar />
      <header>
        <h1 className="approvals-page-title">Location admins</h1>
        <p className="approvals-page-sub">
          Every Location Manager account in the portal and the locations each one manages. Use{" "}
          <strong style={{ color: "#334155" }}>View users</strong> to see the people provisioned under an admin’s
          locations.
        </p>
      </header>

      {data ? <WorkvivoSyncBar sync={data.sync} refresh={workvivoRefresh} /> : null}
      {env.showDirectoryHealthLink ? (
        <div className="directory-actions directory-actions--end">
          <Link to="/directory-health" className="directory-action-link">
            <span aria-hidden>♥</span> Directory health
          </Link>
        </div>
      ) : null}

      {notice ? (
        <div className="directory-notice" role="status">
          <span>{notice}</span>
          <button type="button" className="directory-link-btn" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      <section className="directory-kpis" aria-label="Summary">
        <div className="directory-kpi">
          <p className="directory-kpi-label">Admins</p>
          <p className="directory-kpi-value">{data ? total : "—"}</p>
          <p className="directory-kpi-sub">
            {hasFilter ? "Matching the current filter" : "All Location Manager accounts"}
          </p>
        </div>
        <button
          type="button"
          className="directory-kpi directory-kpi--button"
          aria-expanded={showUnassigned}
          aria-controls={unassignedPanelId}
          data-active={showUnassigned || undefined}
          onClick={() => setShowUnassigned((v) => !v)}
        >
          <span className="directory-kpi-label">Unassigned locations</span>
          <span className="directory-kpi-value">{data ? data.unassigned_store_count : "—"}</span>
          <span className="directory-kpi-sub">
            {showUnassigned ? "Hide list" : "Active locations with no admin — review and assign"}
          </span>
        </button>
        <div className="directory-kpi">
          <p className="directory-kpi-label">Showing</p>
          <p className="directory-kpi-value">{data ? rangeText : "—"}</p>
          <p className="directory-kpi-sub">
            {data ? `of ${total} admin${total === 1 ? "" : "s"}` : "Loading…"}
          </p>
        </div>
      </section>

      {showUnassigned ? <UnassignedStoresPanel id={unassignedPanelId} onNotice={setNotice} /> : null}

      <div className="directory-controls" role="search">
        <label htmlFor={searchId} className="visually-hidden">
          Search location admins
        </label>
        <input
          id={searchId}
          type="search"
          className="directory-search"
          placeholder="Search by name or email"
          value={search.input}
          onChange={(e) => search.setInput(e.target.value)}
          autoComplete="off"
        />
        <label className="directory-toggle">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => {
              setIncludeInactive(e.target.checked);
              setPage(0);
            }}
          />
          Show inactive admins
        </label>
        <PageSizeSelect value={pageSize} onChange={changePageSize} />
        {query.isFetching && !query.isLoading ? (
          <span className="directory-updating" role="status">
            Updating…
          </span>
        ) : null}
      </div>

      {query.isLoading ? (
        <div className="directory-state" role="status">
          Loading location admins…
        </div>
      ) : query.isError ? (
        <div className="directory-error" role="alert">
          <strong>Failed to load location admins.</strong> {extractAxiosDetail(query.error)}
        </div>
      ) : total === 0 ? (
        hasFilter ? (
          <EmptyState
            title="No location admins match"
            description={
              search.query
                ? `Nothing matches “${search.query}”${includeInactive ? "" : " among active admins"}. Try a different name or email.`
                : "No active admins found. Turn on “Show inactive admins” to include deactivated accounts."
            }
          />
        ) : (
          <EmptyState
            title="No location admins yet"
            description="Location Manager accounts appear here once they are provisioned by CSV upload or approved through self-registration."
          />
        )
      ) : (
        <>
          <div
            className="directory-table-wrap"
            aria-busy={query.isPlaceholderData}
            data-stale={query.isPlaceholderData || undefined}
          >
            <table className="directory-table directory-table--admins">
              <thead>
                <tr>
                  <th scope="col">Admin</th>
                  <th scope="col">Locations</th>
                  <th scope="col" className="directory-col-num">
                    Location count
                  </th>
                  <th scope="col" className="directory-col-num">
                    Users managed
                  </th>
                  <th scope="col">Workvivo</th>
                  <th scope="col">Last login</th>
                  <th scope="col" className="directory-col-added">Added</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((admin) => {
                  const name = fullName(admin.first_name, admin.last_name, admin.email);
                  const isSuperuser = admin.user_type === "superuser";
                  return (
                    <tr key={admin.id}>
                      <td data-label="Admin" className="directory-cell-primary">
                        <div className="directory-person">
                          <span className="directory-person-name">{name}</span>
                          <span className="directory-person-email">{admin.email}</span>
                          {isSuperuser || !admin.is_active || admin.force_password_reset ? (
                            <span className="directory-badge-row">
                              {isSuperuser ? (
                                <span className="directory-badge directory-badge--muted">Super user</span>
                              ) : null}
                              {!admin.is_active ? (
                                <span className="directory-badge directory-badge--inactive">Inactive</span>
                              ) : null}
                              {admin.force_password_reset ? (
                                <span className="directory-badge directory-badge--warn">Password reset pending</span>
                              ) : null}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td data-label="Locations">
                        <StoreChips admin={admin} />
                      </td>
                      <td data-label="Location count" className="directory-col-num">
                        <span className="directory-count">{admin.store_count}</span>
                      </td>
                      <td data-label="Users managed" className="directory-col-num">
                        <AdminUsersManagedCell
                          admin={admin}
                          syncStatus={data?.sync?.status}
                          activationChecked={activationChecked}
                        />
                      </td>
                      <td data-label="Workvivo">
                        <AdminWorkvivoCell admin={admin} syncStatus={data?.sync?.status} />
                      </td>
                      <td data-label="Last login">
                        {admin.last_login_at ? (
                          <span title={formatAbsolute(admin.last_login_at)}>
                            {formatRelative(admin.last_login_at) || "—"}
                          </span>
                        ) : (
                          <span className="directory-muted">Never</span>
                        )}
                      </td>
                      <td data-label="Added" className="directory-col-added">
                        <span title={formatAbsolute(admin.created_at)}>{formatRelative(admin.created_at) || "—"}</span>
                      </td>
                      <td className="directory-cell-action">
                        <div className="directory-actions">
                          <Link
                            to={`/location-users?owner=${encodeURIComponent(admin.id)}`}
                            className="directory-action-link"
                            aria-label={`View users managed by ${name}`}
                          >
                            View users <span aria-hidden>→</span>
                          </Link>
                          {isSuperuser ? null : (
                            <button
                              type="button"
                              className="directory-btn directory-btn--small"
                              aria-label={`Resend welcome email to ${name}`}
                              onClick={() => {
                                setResendError(null);
                                setResendFor(admin);
                              }}
                            >
                              <span aria-hidden>✉</span> Resend welcome
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <DirectoryPagination
            page={page}
            pageSize={pageSize}
            total={total}
            offset={offset}
            itemCount={items.length}
            noun="admins"
            onPageChange={setPage}
            onPageSizeChange={changePageSize}
          />
        </>
      )}

      <ConfirmDialog
        open={resendFor != null}
        title="Resend welcome email"
        body={
          <p>
            This resets their temporary password and emails new sign-in details to{" "}
            <strong>{resendFor?.email}</strong>. Continue?
          </p>
        }
        confirmLabel="Resend email"
        isPending={resend.isPending}
        error={resendError}
        onCancel={() => {
          setResendFor(null);
          setResendError(null);
        }}
        onConfirm={async () => {
          if (!resendFor) return;
          setResendError(null);
          try {
            const result = await resend.mutateAsync(resendFor.id);
            setNotice(`Welcome email sent to ${result.email || resendFor.email}.`);
            setResendFor(null);
          } catch (e) {
            setResendError(extractAxiosDetail(e));
          }
        }}
      />
    </div>
  );
}
